// Companion/useChatConversations.ts
//
// Conversation history for the production chat surfaces (ORA-458): the list,
// the switch, the new-conversation reset and the delete. It is the one place
// that turns the v5 conversation endpoints into `conversationHydrated`, so the
// drawer and the page get identical history without either of them fetching.
//
// It is opt-in. `/dev/chat` and the component tests render the shells without
// it and stay entirely offline; production passes the controller down.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  conversationActivated,
  conversationHydrated,
  draftConversationId,
  threadHistorySelectionStarted,
  threadNewRequested,
  threadResolutionFailed,
  threadResolutionFinished,
  threadResolutionStarted,
  type ChatHydratedBlock,
} from '@/slices/chat/chatSlice';
import {
  selectActiveConversationId,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import {
  deleteChatConversation,
  fetchChatConversation,
  fetchChatConversationForAnchor,
  fetchChatConversations,
  updateChatConversation,
  type ChatConversationDetail,
  type ChatConversationSummary,
} from '@/services/chatConversations';
import { useChatAppContextInputs } from '@/chatContext/chatAppContextStore';
import {
  conversationAnchorKey,
  toConversationAnchor,
  toSurfaceConversationAnchor,
} from '@/chatContext/chatThreadKey';
import type { ChatAnchorInput } from '@/types/chatEnvelope';
import { legacyToolBlocksToChatBlocks } from './blocks/legacyChatBlocks';
import { sortConversationSummaries } from './chatConversationOrder';

export interface ChatConversationsController {
  conversations: ChatConversationSummary[];
  /** The conversation the shells are showing, or null for a fresh thread. */
  activeConversationId: string | null;
  isLoading: boolean;
  /** The first list read has finished, or failed; until then an empty list means nothing yet. */
  hasLoaded: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  /** The list could not be read. The shells show it rather than an empty list. */
  error: string | null;
  /** Conversation action failures remain visible while searching history. */
  conversationError: string | null;
  /** The current anchor is being resolved; sending waits for that answer. */
  isResolvingThread: boolean;
  /** The initial anchor or explicit history choice has finished resolving. */
  isThreadReady: boolean;
  /** New Chat is waiting for its first turn to create a distinct thread. */
  shouldStartNewThread: boolean;
  /** Open a conversation: fetch it, hydrate the slice, make it active. */
  select: (conversationId: string) => Promise<void>;
  /** Re-read an open conversation to refresh its time-limited image URLs. */
  reload: (conversationId: string) => Promise<void>;
  /** Start a fresh thread. The server assigns its id on the first `turn.start`. */
  startNew: () => void;
  remove: (conversationId: string) => Promise<void>;
  /** Change a title after explicit student confirmation in the sidebar dialog. */
  rename: (conversationId: string, title: string) => Promise<void>;
  /** Persist a pin and immediately regroup the local list from server time. */
  setPinned: (conversationId: string, pinned: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  /** Re-read the list — after a first turn names a new conversation, say. */
  refresh: () => Promise<void>;
}

/**
 * Group the old panel's persisted tool blocks by the message they belonged to
 * and map them onto the block contract. Legacy conversations only; a block
 * whose kind has no modern counterpart is dropped by the mapper.
 */
const legacyBlocksByMessage = (
  detail: ChatConversationDetail
): Map<string, ChatHydratedBlock[]> => {
  const grouped = new Map<string, ChatHydratedBlock[]>();

  detail.legacyToolBlocks.forEach((block) => {
    const mapped = legacyToolBlocksToChatBlocks([block]);
    if (mapped.length === 0) return;

    grouped.set(block.messageId, [
      ...(grouped.get(block.messageId) ?? []),
      ...mapped,
    ]);
  });

  return grouped;
};

/** The `conversationHydrated` payload for one fetched conversation. */
export const toHydratedConversation = (detail: ChatConversationDetail) => {
  const legacyBlocks = detail.legacy
    ? legacyBlocksByMessage(detail)
    : new Map<string, ChatHydratedBlock[]>();

  return {
    id: detail.id,
    title: detail.title,
    legacy: detail.legacy,
    messages: detail.messages.map((message) => ({
      id: message.id,
      role: message.role,
      text: message.text,
      attachments: message.attachments.map((attachment) => ({
        id: attachment.id,
        mimeType: attachment.mimeType,
        ...(attachment.kind ? { kind: attachment.kind } : {}),
        ...(attachment.documentId ? { documentId: attachment.documentId } : {}),
        ...(attachment.fileName ? { fileName: attachment.fileName } : {}),
        ...(attachment.pageCount !== undefined
          ? { pageCount: attachment.pageCount }
          : {}),
        previewUrl: attachment.url,
        ...(attachment.key ? { key: attachment.key } : {}),
        ...(attachment.sizeBytes !== undefined
          ? { sizeBytes: attachment.sizeBytes }
          : {}),
      })),
      citations: message.citations,
      blocks:
        message.blocks.length > 0
          ? message.blocks
          : (legacyBlocks.get(message.id) ?? []),
      // The trail and the chips the answer showed live (ORA-604, ORA-606).
      // Passed through as-is: the slice rebuilds the finished turn from them.
      ...(message.followups.length > 0 ? { followups: message.followups } : {}),
      ...(message.activity ? { activity: message.activity } : {}),
      ...(message.contextLabel ? { contextLabel: message.contextLabel } : {}),
      ...(message.actionProposal
        ? {
            actionProposal: {
              ...message.actionProposal,
              turnId: message.id,
            },
          }
        : {}),
    })),
  };
};

export interface UseChatConversationsOptions {
  /**
   * False keeps the hook inert — no fetch, an empty list. The shells still
   * render, which is what the flag-off build and the offline dev page need.
   */
  enabled?: boolean;
  /** Resolve the generic or anchored thread as the visible anchor changes. */
  threading?: boolean;
  /** `/chat` is always generic, even if a drawer anchor has not retracted yet. */
  threadAnchor?: ChatAnchorInput | null;
}

export const useChatConversations = ({
  enabled = true,
  threading = false,
  threadAnchor,
}: UseChatConversationsOptions = {}): ChatConversationsController => {
  const dispatch = useDispatch();
  const store = useStore<ChatRootState>();
  const activeConversationId = useSelector(selectActiveConversationId);

  // The surface's anchor, never a block's: a served question answered inside
  // the thread must not move the thread (ORA-628). In a course session the
  // published module is the thread instead (ORA-1055).
  const { surfaceAnchor: publishedAnchor, course: publishedCourse } =
    useChatAppContextInputs();

  const currentAnchor = useMemo(
    () =>
      threadAnchor === undefined
        ? toSurfaceConversationAnchor(publishedAnchor, publishedCourse)
        : toConversationAnchor(threadAnchor),
    [publishedAnchor, publishedCourse, threadAnchor]
  );

  const currentAnchorKey = conversationAnchorKey(currentAnchor);
  const currentAnchorRef = useRef(currentAnchor);
  currentAnchorRef.current = currentAnchor;

  const threadTargetKey = useSelector(
    (state: ChatRootState) => state.chat.threadTargetKey
  );

  const threadResolutionId = useSelector(
    (state: ChatRootState) => state.chat.threadResolutionId
  );

  const isResolvingThread = useSelector(
    (state: ChatRootState) => state.chat.isThreadResolving
  );

  const threadResolutionFailedKey = useSelector(
    (state: ChatRootState) => state.chat.threadResolutionFailedKey
  );

  const newThreadKey = useSelector(
    (state: ChatRootState) => state.chat.newThreadKey
  );

  const [conversations, setConversations] = useState<ChatConversationSummary[]>(
    []
  );

  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const shouldStartNewThread = newThreadKey === currentAnchorKey;

  const isThreadReady =
    !threading || (threadTargetKey === currentAnchorKey && !isResolvingThread);

  /** Deletes in flight, so a double click cannot fire two requests. */
  const deletingRef = useRef(new Set<string>());
  /** A late generated-title stream event must not undo an explicit rename. */
  const manualTitlesRef = useRef(new Map<string, string>());
  const wasThreadingEnabledRef = useRef(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!enabled) return;

      setIsLoading(true);
      try {
        const page = await fetchChatConversations(undefined, signal);
        if (signal?.aborted) return;
        setConversations(sortConversationSummaries(page.conversations));
        setHasMore(page.hasMore);
        setHistoryError(null);
      } catch (caught) {
        if (signal?.aborted) return;
        setHistoryError('Chat history is unavailable right now.');
        console.error('Unable to load chat conversations', caught);
      } finally {
        if (!signal?.aborted) {
          setIsLoading(false);
          setHasLoaded(true);
        }
      }
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    void load(controller.signal);

    return () => controller.abort();
  }, [enabled, load]);

  // A layout effect: a new anchor's lookup starts before the browser paints,
  // so the surface shows its resolving state rather than, for a frame, the
  // previous anchor's thread (or an empty new chat) under the new anchor — a
  // View Document session opening beside the old conversation (ORA-736).
  useLayoutEffect(() => {
    if (!enabled || !threading) {
      wasThreadingEnabledRef.current = false;
      return;
    }

    const reopenedAfterFailure =
      !wasThreadingEnabledRef.current &&
      threadResolutionFailedKey === currentAnchorKey;

    wasThreadingEnabledRef.current = true;

    // The shared slice has either started, completed, or deliberately skipped
    // this exact target. A second visible shell observes that state and does
    // not open another request that can later overwrite the first result.
    if (threadTargetKey === currentAnchorKey && !reopenedAfterFailure) return;

    const resolutionId = uuidv4();
    setError(null);
    dispatch(threadResolutionStarted({ key: currentAnchorKey, resolutionId }));

    void fetchChatConversationForAnchor(currentAnchorRef.current)
      .then((detail) => {
        const current = store.getState().chat;
        if (
          current.threadTargetKey !== currentAnchorKey ||
          current.threadResolutionId !== resolutionId
        )
          return;

        if (detail) {
          dispatch(conversationHydrated(toHydratedConversation(detail)));
          dispatch(conversationActivated(detail.id));
        } else dispatch(conversationActivated(null));
        setError(null);
      })
      .catch((caught) => {
        const current = store.getState().chat;
        if (
          current.threadTargetKey !== currentAnchorKey ||
          current.threadResolutionId !== resolutionId
        )
          return;

        dispatch(
          threadResolutionFailed({ key: currentAnchorKey, resolutionId })
        );

        setError('That chat thread could not be opened.');
        console.error('Unable to resolve chat thread', caught);
      })
      .finally(() => {
        dispatch(
          threadResolutionFinished({ key: currentAnchorKey, resolutionId })
        );
      });
  }, [
    currentAnchorKey,
    dispatch,
    enabled,
    store,
    threadResolutionFailedKey,
    threadResolutionId,
    threadTargetKey,
    threading,
  ]);

  /**
   * A conversation the student has just started exists on the server only once
   * its first turn opened it, and the list was read before that — so its row
   * has to be put there from what this client already knows.
   *
   * It is built from the chat slice (the title arrives on the stream's own
   * `title` event) rather than by re-reading the list. Re-reading was the
   * original design and it was wrong twice over: the row is not on page one
   * for a deep link into older history, and the server hides a conversation
   * with no messages yet — so the id stayed unknown, the list state changed on
   * every response, and the effect re-fired forever.
   */
  const activeTitle = useSelector((state: ChatRootState) =>
    activeConversationId
      ? (state.chat.conversations[activeConversationId]?.title ?? null)
      : null
  );

  useEffect(() => {
    if (!enabled || !activeConversationId) return;
    // A draft conversation id is the slice's own, not the server's; it is
    // re-keyed on `turn.start` and means nothing to history until then.
    if (activeConversationId.startsWith(draftConversationId(''))) return;

    const title =
      manualTitlesRef.current.get(activeConversationId) ?? activeTitle;

    setConversations((current) => {
      const index = current.findIndex(
        (item) => item.id === activeConversationId
      );

      if (index === -1) {
        return [
          {
            id: activeConversationId,
            title,
            lastMessageAt: new Date().toISOString(),
            pinnedAt: null,
            legacy: false,
          },
          ...current,
        ];
      }

      // A row that is already there keeps everything it knows; only the title
      // can still be arriving, and an absent one never blanks a real one.
      if (title === null || current[index].title === title) {
        return current;
      }

      const next = [...current];
      next[index] = { ...next[index], title };
      return next;
    });
  }, [activeConversationId, activeTitle, enabled]);

  const select = useCallback(
    async (conversationId: string) => {
      setError(null);
      const current = store.getState().chat;
      if (
        conversationId === current.activeConversationId &&
        current.threadTargetKey === currentAnchorKey &&
        !current.isThreadResolving
      )
        return;

      const resolutionId = uuidv4();

      // Activate first: the thread switches immediately and fills in when the
      // fetch lands, rather than leaving the previous conversation on screen.
      dispatch(
        threadHistorySelectionStarted({
          key: currentAnchorKey,
          resolutionId,
          conversationId,
        })
      );

      try {
        const detail = await fetchChatConversation(conversationId);
        const latest = store.getState().chat;
        if (
          latest.threadTargetKey !== currentAnchorKey ||
          latest.threadResolutionId !== resolutionId
        )
          return;

        if (!detail) {
          setConversations((current) =>
            current.filter((item) => item.id !== conversationId)
          );

          dispatch(conversationActivated(null));
          return;
        }

        dispatch(conversationHydrated(toHydratedConversation(detail)));

        // A deep link can open a conversation the list has never seen — one
        // past the first page, or one the list filter hides. Now that it has
        // been read, its row carries what the server actually said about it.
        setConversations((current) => {
          const index = current.findIndex((item) => item.id === detail.id);
          if (index === -1) return current;

          const row = current[index];

          if (row.title === detail.title && row.legacy === detail.legacy) {
            return current;
          }

          const next = [...current];
          next[index] = { ...row, title: detail.title, legacy: detail.legacy };
          return next;
        });
      } catch (caught) {
        const latest = store.getState().chat;
        if (
          latest.threadTargetKey !== currentAnchorKey ||
          latest.threadResolutionId !== resolutionId
        )
          return;

        dispatch(
          threadResolutionFailed({ key: currentAnchorKey, resolutionId })
        );

        setError('That conversation could not be opened.');
        console.error('Unable to open chat conversation', caught);
      } finally {
        dispatch(
          threadResolutionFinished({ key: currentAnchorKey, resolutionId })
        );
      }
    },
    [currentAnchorKey, dispatch, store]
  );

  /** Each conversation reload only supersedes an earlier reload of itself. */
  const reloadIds = useRef(new Map<string, string>());

  const reload = useCallback(
    async (conversationId: string) => {
      setError(null);
      if (!enabled) return;

      const reloadId = uuidv4();
      reloadIds.current.set(conversationId, reloadId);

      try {
        const detail = await fetchChatConversation(conversationId);
        if (reloadIds.current.get(conversationId) !== reloadId) return;

        if (!detail) {
          setConversations((current) =>
            current.filter((item) => item.id !== conversationId)
          );

          if (activeConversationId === conversationId)
            dispatch(conversationActivated(null));
          return;
        }

        dispatch(conversationHydrated(toHydratedConversation(detail)));
      } catch (caught) {
        if (reloadIds.current.get(conversationId) !== reloadId) return;
        setError('That conversation could not be refreshed.');
        console.error('Unable to refresh chat conversation', caught);
      } finally {
        if (reloadIds.current.get(conversationId) === reloadId) {
          reloadIds.current.delete(conversationId);
        }
      }
    },
    [activeConversationId, dispatch, enabled]
  );

  const startNew = useCallback(() => {
    setError(null);
    dispatch(threadNewRequested(currentAnchorKey));
  }, [currentAnchorKey, dispatch]);

  const remove = useCallback(
    async (conversationId: string) => {
      if (deletingRef.current.has(conversationId)) return;
      setError(null);
      deletingRef.current.add(conversationId);

      try {
        await deleteChatConversation(conversationId);

        setConversations((current) =>
          current.filter((item) => item.id !== conversationId)
        );

        if (conversationId === activeConversationId) startNew();
      } catch (caught) {
        setError('That conversation could not be deleted.');
        console.error('Unable to delete chat conversation', caught);
      } finally {
        deletingRef.current.delete(conversationId);
      }
    },
    [activeConversationId, startNew]
  );

  const rename = useCallback(async (conversationId: string, title: string) => {
    setError(null);
    try {
      const updated = await updateChatConversation(conversationId, { title });
      if (updated.title) manualTitlesRef.current.set(updated.id, updated.title);

      setConversations((current) =>
        current.map((conversation) =>
          conversation.id === updated.id
            ? { ...conversation, title: updated.title }
            : conversation
        )
      );
    } catch (caught) {
      setError('That conversation could not be renamed.');
      console.error('Unable to rename chat conversation', caught);
    }
  }, []);

  const setPinned = useCallback(
    async (conversationId: string, pinned: boolean) => {
      setError(null);
      try {
        const updated = await updateChatConversation(conversationId, {
          pinned,
        });

        setConversations((current) =>
          sortConversationSummaries(
            current.map((conversation) =>
              conversation.id === updated.id
                ? {
                    ...conversation,
                    title: updated.title,
                    pinnedAt: updated.pinnedAt,
                  }
                : conversation
            )
          )
        );

        // Pin order is part of the API's pagination. Once a row crosses a
        // page boundary, the old offset can skip the row that took its place,
        // so restart from the first server page before offering Load more.
        await load();
      } catch (caught) {
        setError(
          pinned
            ? 'That conversation could not be pinned.'
            : 'That conversation could not be unpinned.'
        );

        console.error('Unable to update chat conversation pin', caught);
      }
    },
    [load]
  );

  const loadMore = useCallback(async () => {
    if (!enabled || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    setHistoryError(null);
    try {
      const page = await fetchChatConversations({
        skip: conversations.length,
      });

      // Merge by id: a conversation that moved between pages must not double.
      setConversations((current) => {
        const seen = new Set(current.map((item) => item.id));
        return sortConversationSummaries([
          ...current,
          ...page.conversations.filter((item) => !seen.has(item.id)),
        ]);
      });

      setHasMore(page.hasMore);
    } catch (caught) {
      // Surfaced, not just logged: a "Load more" that quietly does nothing
      // reads as "there is no more history", which is a different thing.
      setHistoryError('More chats could not be loaded.');
      console.error('Unable to load more chat conversations', caught);
    } finally {
      setIsLoadingMore(false);
    }
  }, [conversations.length, enabled, hasMore, isLoadingMore]);

  const refresh = useCallback(() => load(), [load]);

  return {
    conversations,
    activeConversationId,
    isLoading,
    hasLoaded,
    isLoadingMore,
    hasMore,
    error: error ?? historyError,
    conversationError: error,
    isResolvingThread,
    isThreadReady,
    shouldStartNewThread,
    select,
    reload,
    startNew,
    remove,
    rename,
    setPinned,
    loadMore,
    refresh,
  };
};
